import React, { useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useScrollProgress, useElementScrollProgress } from "../hooks/useScrollProgress";
import { GeometryBackground } from "../components/lander/GeometryBackground";
import { SankhyaHero } from "../components/lander/SankhyaHero";
import { ScrollReveal } from "../components/lander/ScrollReveal";
import { Preloader } from "../components/lander/Preloader";
import { MouseGlow } from "../components/lander/MouseGlow";
import { ScrollNav } from "../components/lander/ScrollNav";

// Import styles
import "../styles/lander.css";

export default function LandingPage() {
  const scrollProgress = useScrollProgress();
  const [loaded, setLoaded] = useState(false);
  const handlePreloaderComplete = useCallback(() => setLoaded(true), []);

  return (
    <>
      <Preloader onComplete={handlePreloaderComplete} />
      <div
        className="sankhya-page relative"
        style={{
          background: "oklch(0.97 0.01 280)",
          opacity: loaded ? 1 : 0,
          transition: "opacity 0.5s ease-out",
        }}
      >
        <MouseGlow />
        <GeometryBackground scrollProgress={scrollProgress} />
        <ScrollNav />

        <SankhyaHero />
        <PhilosophySection />
        <ShiftSection />
        <EssenceSection />
        <CTASection />
      </div>
    </>
  );
}

function PhilosophySection() {
  return (
    <section className="relative min-h-screen flex items-center justify-center px-6">
      <div className="max-w-3xl text-center">
        <ScrollReveal direction="z-in" distance={50}>
          <h2
            className="text-2xl md:text-5xl font-light leading-relaxed tracking-wide"
            style={{ color: "oklch(0.28 0.04 280)" }}
          >
            "From complexity to clarity —
            <br />
            <span className="italic" style={{ color: "oklch(0.42 0.07 160)" }}>
              built around your intent,
            </span>
            <br />
            not the market."
          </h2>
        </ScrollReveal>

        <ScrollReveal direction="up" delay={200} distance={30}>
          <div className="mt-14 space-y-5">
            {[
              "It doesn't shout. It knows.",
              "The system remembers what you meant, not just what you clicked.",
              "Everything anchored to intent + behavior.",
            ].map((text, i) => (
              <p
                key={i}
                className="text-sm md:text-base tracking-[0.12em] font-light jp-lander-text-muted"
              >
                {text}
              </p>
            ))}
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}

function ShiftSection() {
  const ref = useRef(null);
  const progress = useElementScrollProgress(ref);
  const tilt = Math.min(Math.max((progress - 0.2) * 2.5, 0), 1);

  return (
    <section
      ref={ref}
      className="relative min-h-screen flex items-center justify-center px-6"
    >
      <div className="grid md:grid-cols-2 gap-8 md:gap-12 max-w-4xl w-full">
        <ScrollReveal direction="left" delay={0}>
          <div
            className="p-8 md:p-10 rounded-2xl lander-glass"
            style={{
              transform: `perspective(800px) rotateY(${tilt * -5}deg)`,
              transition: "transform 0.4s ease-out",
            }}
          >
            <h3
              className="text-base md:text-lg tracking-[0.2em] uppercase font-light mb-6"
              style={{ color: "oklch(0.5 0.03 280)" }}
            >
              The Old Way
            </h3>
            {["Search", "Apply", "Hope"].map((word, i) => (
              <p
                key={word}
                className="text-lg md:text-xl font-light mb-3 line-through"
                style={{
                  color: "oklch(0.6 0.02 280 / 0.5)",
                  textDecorationColor: "oklch(0.65 0.12 25 / 0.4)",
                }}
              >
                {word} {i < 2 ? "→" : ""}
              </p>
            ))}
            <p className="mt-5 text-xs tracking-[0.18em] uppercase" style={{ color: "oklch(0.6 0.02 280 / 0.5)" }}>
              Volume over clarity
            </p>
          </div>
        </ScrollReveal>

        <ScrollReveal direction="right" delay={150}>
          <div
            className="p-8 md:p-10 rounded-2xl lander-glass-alt"
            style={{
              transform: `perspective(800px) rotateY(${tilt * 5}deg)`,
              transition: "transform 0.4s ease-out",
            }}
          >
            <h3
              className="text-base md:text-lg tracking-[0.2em] uppercase font-light mb-6"
              style={{ color: "oklch(0.35 0.05 160)" }}
            >
              The New Lens
            </h3>
            {["Align", "Decide", "Progress"].map((word, i) => (
              <p
                key={word}
                className="text-lg md:text-xl font-light mb-3"
                style={{ color: "oklch(0.28 0.04 280)" }}
              >
                {word} {i < 2 ? "→" : ""}
              </p>
            ))}
            <p className="mt-5 text-xs tracking-[0.18em] uppercase" style={{ color: "oklch(0.42 0.05 160)" }}>
              Clarity over volume
            </p>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}

function EssenceSection() {
  return (
    <section className="relative min-h-screen flex items-center justify-center px-6">
      <div className="text-center max-w-3xl">
        <ScrollReveal direction="z-in" distance={60}>
          <h2
            className="text-3xl md:text-6xl font-extralight leading-tight"
            style={{ color: "oklch(0.25 0.04 280)", letterSpacing: "0.04em" }}
          >
            Stop searching.
          </h2>
        </ScrollReveal>
        <ScrollReveal direction="z-in" distance={60} delay={250}>
          <h2
            className="text-3xl md:text-6xl font-extralight leading-tight mt-3"
            style={{ color: "oklch(0.4 0.08 160)", letterSpacing: "0.04em" }}
          >
            Start progressing.
          </h2>
        </ScrollReveal>

        <ScrollReveal direction="up" delay={500} distance={25}>
          <div className="mt-14 space-y-3">
            <p className="text-sm tracking-[0.15em] font-light" style={{ color: "oklch(0.45 0.03 280)" }}>
              "Karmanye vadhikaraste, ma phaleshu kadachana"
            </p>
            <p className="text-xs tracking-[0.12em] italic" style={{ color: "oklch(0.52 0.04 160)" }}>
              Focus on action, not outcome — Bhagavad Gita
            </p>
          </div>
        </ScrollReveal>

        <ScrollReveal direction="up" delay={700} distance={20}>
          <div className="mt-10 flex flex-wrap justify-center gap-8">
            {[
              { label: "Fewer actions", sub: "higher quality" },
              { label: "Stronger conviction", sub: "less noise" },
              { label: "Compounding outcomes", sub: "over time" },
            ].map(({ label, sub }) => (
              <div key={label} className="text-center">
                <p className="text-sm font-medium tracking-[0.08em]" style={{ color: "oklch(0.32 0.04 280)" }}>
                  {label}
                </p>
                <p className="text-xs tracking-[0.1em] mt-1" style={{ color: "oklch(0.55 0.03 160)" }}>
                  {sub}
                </p>
              </div>
            ))}
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}

function CTASection() {
  return (
    <section className="relative min-h-[60vh] flex items-center justify-center px-6">
      <ScrollReveal direction="z-in" distance={50}>
        <div className="text-center">
          <p
            className="text-lg md:text-2xl font-light tracking-[0.18em]"
            style={{ color: "oklch(0.38 0.04 280)" }}
          >
            Less browsing. More knowing.
          </p>
          <div className="mt-8">
            <Link to="/signup">
                <button
                className="group relative px-10 py-4 rounded-full text-sm tracking-[0.2em] uppercase font-light overflow-hidden transition-all duration-500 hover:shadow-lg cursor-pointer"
                style={{
                    background: "linear-gradient(135deg, oklch(0.92 0.05 280 / 0.7), oklch(0.9 0.04 160 / 0.7))",
                    border: "1px solid oklch(0.82 0.06 280 / 0.35)",
                    color: "oklch(0.3 0.04 280)",
                    boxShadow: "0 8px 32px -8px oklch(0.65 0.08 280 / 0.18)",
                }}
                >
                <span className="relative z-10">Join the Waitlist</span>
                <div
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    style={{
                    background: "linear-gradient(135deg, oklch(0.87 0.08 280 / 0.8), oklch(0.85 0.06 160 / 0.8))",
                    }}
                />
                </button>
            </Link>
          </div>
          <p
            className="mt-6 text-xs tracking-[0.15em]"
            style={{ color: "oklch(0.55 0.03 280 / 0.6)" }}
          >
            "Same listings. New lens — alignment tracking, not job tracking."
          </p>
        </div>
      </ScrollReveal>
    </section>
  );
}
